import Navbar from '@/components/Navbar';

/**
 * Phase 9.3 unified loading primitive. CSS-only, no spinners, no Lucide icons.
 * Replaces the project-wide `Loader2` spinner pattern and the table-shaped
 * Suspense fallback for non-table routes.
 *
 * Variants:
 *   - boot:  full-screen brand frame (reserved for code parity; the actual
 *            initial paint is the prerender hero in index.html).
 *   - route: neutral page shell used as the App-level Suspense fallback.
 *            Renders the Navbar + a single shimmer block sized to the
 *            viewport so non-table routes (/pricing, /about, /track-record,
 *            /leaderboard, /calendar, /stock/*, /insider/*, /politician/*)
 *            no longer flash a fake table skeleton.
 *   - panel: inline shimmer block that fills its parent container. Use
 *            anywhere the project previously rendered a centered Loader2.
 */
type Variant = 'boot' | 'route' | 'panel';

interface LoadingShellProps {
  variant?: Variant;
  /** Optional override class for the shimmer block (panel/boot only). */
  className?: string;
  /** Approximate min-height for the panel variant. Defaults to 12rem. */
  minHeight?: string;
}

const LoadingShell = ({ variant = 'panel', className, minHeight }: LoadingShellProps) => {
  if (variant === 'route') {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-4 py-4 md:px-6 md:py-6">
          <div
            className="shell-shimmer rounded-lg border border-border"
            style={{ minHeight: '60vh' }}
            aria-hidden="true"
          />
        </main>
      </div>
    );
  }

  if (variant === 'boot') {
    return (
      <div className="min-h-screen bg-background p-4">
        <div
          className="shell-shimmer rounded-lg border border-border"
          style={{ minHeight: '70vh' }}
          aria-hidden="true"
        />
      </div>
    );
  }

  // panel
  return (
    <div
      className={`shell-shimmer rounded-lg border border-border ${className ?? ''}`.trim()}
      style={{ minHeight: minHeight ?? '12rem' }}
      aria-hidden="true"
    />
  );
};

export default LoadingShell;
